// PB-002 — the human sentences for the invitation acceptance screen. The
// server's refusal codes (apps/dashboard/src/routes/invite.ts `denial`) are
// mapped here so no raw code ever reaches the page.

export interface InvitePeek {
  businessName: string;
  emailDisplay: string;
  role: string;
  expiresAt: string;
}

export type AcceptFailure =
  | { kind: 'invalid' }
  | { kind: 'message'; text: string; signOut?: boolean; dashboard?: boolean };

export function roleLabel(role: string): string {
  if (role === 'manager') return 'a manager';
  if (role === 'editor') return 'an editor';
  return 'a team member';
}

/** Map an accept-call error (lib/api.ts ApiError: status + JSON body) to what the screen says. */
export function acceptFailure(err: unknown, peek: InvitePeek, signedInEmail: string | null): AcceptFailure {
  const status = (err as { status?: number })?.status;
  const code = (err as { body?: { code?: unknown } })?.body?.code;
  if (status === 404) return { kind: 'invalid' };
  if (code === 'invitation_email_mismatch') {
    const who = signedInEmail ? `you're signed in as ${signedInEmail}` : "you're signed in with a different address";
    return {
      kind: 'message',
      text: `This invitation was sent to ${peek.emailDisplay}, but ${who}. Sign out, open this link again, and sign in with ${peek.emailDisplay}.`,
      signOut: true,
    };
  }
  if (code === 'identity_unverified') {
    return {
      kind: 'message',
      text: `We couldn't confirm the email address on your account. Sign out, open this link again, and sign in with Google or Apple using ${peek.emailDisplay}.`,
      signOut: true,
    };
  }
  if (code === 'ALREADY_MEMBER' || code === 'INVITATION_ALREADY_ACCEPTED') {
    return { kind: 'message', text: `You're already on the ${peek.businessName} team.`, dashboard: true };
  }
  if (code === 'MEMBER_CAP_EXCEEDED') {
    return { kind: 'message', text: `The ${peek.businessName} team is full right now. Ask the person who invited you to free up a seat, then try again.` };
  }
  if (typeof status !== 'number' || status >= 500) {
    return { kind: 'message', text: "Something went wrong on our side. Please try again in a moment." };
  }
  return { kind: 'message', text: "We couldn't accept this invitation. Ask the person who invited you to send a new link." };
}
